import React, { useCallback, useState } from 'react'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { IconCheck, IconCopy, IconDownload, IconX } from '@tabler/icons-react'
import FilePreviewModal from './components/FilePreviewModal'
import { fixImgSrc } from '../../../utils/tools'
import { FileExtensionImg } from './components/FileExtensionImg'
import './style.scss'

interface IFileViewRowProps {
  fileSource: string
  fileName: string
  onRemove?: () => void
  hasPreview?: boolean
  fixSrc?: boolean
}

const TooltipIconButton: React.FC<{
  title: string
  onClick: () => void
  children: React.ReactNode
  fixSrc?: boolean
}> = ({ title, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton onClick={onClick}>{children}</IconButton>
  </Tooltip>
)

const FileViewRow: React.FC<IFileViewRowProps> = ({
  fileSource,
  onRemove,
  fileName,
  hasPreview,
  fixSrc = true,
}) => {
  const [linkCopied, setLinkCopied] = useState(false)
  const [open, setOpen] = useState(false)
  const fixedFileSource = fixSrc ? fixImgSrc(fileSource) : fileSource

  const handleOpenImage = useCallback(() => {
    setOpen(true)
  }, [])
  const truncateFileName = useCallback(() => {
    const name = fileName
    if (name.length > 20) {
      return `${name.substring(0, 20)}...`
    }
    return name
  }, [fileName])

  const handleLinkCopy = useCallback(() => {
    navigator.clipboard.writeText(fixedFileSource)
    setLinkCopied(true)
  }, [fixedFileSource])

  // Extract file type from fileName or fileSource
  let fileType = ''

  // Try to get file type from fileName first
  if (fileName && fileName.includes('.')) {
    fileType = fileName.split('.').reverse()[0]
  } else if (fileSource) {
    // If no extension in fileName, try to extract from URL
    const urlParts = fileSource.split('.')
    if (urlParts.length > 1) {
      fileType = urlParts.pop() || ''
      // Remove any query parameters
      fileType = fileType.split('?')[0]
    }
  }

  const handleDownload = useCallback(() => {
    // Create XHR request
    const xhr = new XMLHttpRequest()
    xhr.open('GET', fixedFileSource, true)
    xhr.responseType = 'blob'

    xhr.onload = function () {
      if (this.status === 200) {
        // Create blob with correct content type
        let contentType = 'image/jpeg' // Default content type

        // Try to detect content type from extension
        if (fileType) {
          switch (fileType.toLowerCase()) {
            case 'pdf':
              contentType = 'application/pdf'
              break
            case 'png':
              contentType = 'image/png'
              break
            case 'jpg':
            case 'jpeg':
              contentType = 'image/jpeg'
              break
            case 'gif':
              contentType = 'image/gif'
              break
            case 'mp4':
              contentType = 'video/mp4'
              break
            case 'mp3':
              contentType = 'audio/mpeg'
              break
            case 'docx':
              contentType =
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              break
            case 'xlsx':
              contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              break
            case 'pptx':
              contentType =
                'application/vnd.openxmlformats-officedocument.presentationml.presentation'
              break
            case 'txt':
              contentType = 'text/plain'
              break
            case 'csv':
              contentType = 'text/csv'
              break
            case 'html':
              contentType = 'text/html'
              break
            case 'json':
              contentType = 'application/json'
              break
            case 'xml':
              contentType = 'application/xml'
              break
            default:
              contentType = 'application/octet-stream'
              break
          }
        }

        const blob = new Blob([this.response], { type: contentType })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()

        // Clean up
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }
    }

    xhr.send()
  }, [fixedFileSource, fileName, fileType])

  return (
    <div className="FileViewRow">
      <div className="iconAndName">
        {hasPreview ? (
          <TooltipIconButton title="Preview" onClick={handleOpenImage}>
            <FileExtensionImg fileType={fileType} />
          </TooltipIconButton>
        ) : (
          <FileExtensionImg fileType={fileType} />
        )}
        <Typography
          onClick={() => {
            if (hasPreview) handleOpenImage()
          }}
          className="fileName"
        >
          {truncateFileName() || 'Image file'}
        </Typography>
      </div>
      <div className="icons">
        {linkCopied ? (
          <TooltipIconButton title="Copied" onClick={handleLinkCopy}>
            <IconCheck />
          </TooltipIconButton>
        ) : (
          <TooltipIconButton title="Copy to clipboard" onClick={handleLinkCopy}>
            <IconCopy className="iconCopy" />
          </TooltipIconButton>
        )}
        <TooltipIconButton title="Download" onClick={handleDownload}>
          <IconDownload color="blue" />
        </TooltipIconButton>
        {onRemove && (
          <TooltipIconButton title="Remove" onClick={onRemove}>
            <IconX color="red" className="removeIcon" />
          </TooltipIconButton>
        )}
      </div>
      <FilePreviewModal
        open={open}
        setOpen={setOpen}
        fixedFileSource={fixedFileSource}
        fileType={fileType}
      />
    </div>
  )
}

export default FileViewRow
